import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { AbstractLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/abstract-layers-table.component';
import { FolderAndStandardByDepositId } from '@connect-services/client/components/project-settings/project-layers-settings/project-layers-settings.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { DepositsService, DepositWithFolderAndStandard, Layer, LayersService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        PaginationFooterTemplateComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
    ],
    selector: 'app-deposit-layers-table',
    styleUrl: '../project-layers-settings.component.scss',
    templateUrl: './deposit-layers-table.component.html',
})
export class DepositLayersTableComponent extends AbstractLayersTableComponent implements OnInit {
    public readonly geomOnly = input<boolean>(true);
    public readonly layerType = input<Layer.TypeEnum>(Layer.TypeEnum.TABLE);
    public readonly searchValue = input<string>('');
    public updateDepositCount = output<number>();

    protected readonly deposits: WritableSignal<FolderAndStandardByDepositId> = signal(new Map());
    protected tableColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'layer',
        },
        {
            key: 'deposit',
            label: 'data',
        },
        {
            key: 'versionLock',
            label: 'version',
        },
        {
            key: 'standard',
            label: 'standard',
            sortable: false,
        },
    ];

    private readonly depositServiceSdk = inject(DepositsService);
    private readonly layersServiceSdk = inject(LayersService);

    public constructor() {
        super();

        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            this.searchValue();
            this.layerService.forceLayerListRefresh();

            untracked(() => this.loadLayers());
        });
    }

    public ngOnInit(): void {
        if (!this.geomOnly()) {
            return;
        }

        this.tableColumns.push(
            {
                key: 'order',
                label: 'order',
            },
            {
                key: 'isVisible',
                label: 'visibility',
            },
        );
    }

    protected override async loadLayers(): Promise<void> {
        this.isLoading.set(true);
        const isReferenceData = this.layerType() === Layer.TypeEnum.REFERENCE_DATA;

        const loadedLayers = await firstValueFrom(
            this.layersServiceSdk.layerControllerGet({
                project: this.projectBloc.value()!.id,
                searchValue: this.searchValue(),
                types: [isReferenceData ? this.LayerTypeEnum.REFERENCE_DATA : this.LayerTypeEnum.TABLE],
                ...(!isReferenceData && {
                    geomFilter: this.geomOnly() ? 'ONLY_GEOM' : 'ONLY_NOGEOM',
                }),
                ...this.paginationParameters(),
            }),
        );

        this.paginationParameters.update((parameters) => {
            return {
                ...parameters,
                hasNextPage: loadedLayers.hasNextPage,
                hasPreviousPage: loadedLayers.hasPreviousPage,
                page: loadedLayers.page,
                totalItems: loadedLayers.totalItems,
                totalPages: loadedLayers.totalPages,
            };
        });

        this.layers.set(loadedLayers!.items);

        await this.loadDeposits();

        this.updateDepositCount.emit(this.paginationParameters().totalItems ?? 0);
        this.isLoading.set(false);
    }

    private async loadDeposits(): Promise<void> {
        for (const layer of this.layers()) {
            if (!layer?.deposit || this.deposits().has(layer.deposit)) {
                continue;
            }

            const deposit: DepositWithFolderAndStandard = await firstValueFrom(
                this.depositServiceSdk.depositControllerGetDepositWithFolderAndStandard({ id: layer.deposit }),
            );

            this.deposits.update((deposits) => {
                const newDeposits = new Map(deposits);
                newDeposits.set(deposit.id, deposit);

                return newDeposits;
            });
        }
    }
}
